<template>
  <div ref="chartRoot" class="benchmark-chart-container">
    <div class="benchmark-chart-card">
      <vue-apex-charts
        :key="chartKey"
        type="bar"
        height="300"
        width="500"
        :options="chartOptions"
        :series="series"
      />
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'BenchmarkBarChart',

  components: { VueApexCharts },

  data () {
    return {
      animated: false,
      chartKey: 0,

      series: [
        {
          name: 'Value',
          data: [2, 3, 1.5, 4, 7, 9, 12, 14, 16, 18, 20, 22, 24, 27, 30, 35, 37.5, 39.1, 33]
        }
      ],

      chartOptions: {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          background: 'transparent',
          animations: {
            enabled: true,
            speed: 900,
            easing: 'easeinout',
            animateGradually: { enabled: true, delay: 80 },
            dynamicAnimation: { enabled: true, speed: 400 }
          },
          events: {
            mounted: (chartContext) => {
              setTimeout(() => this._drawConnector(chartContext), 1100)
            },
            updated: (chartContext) => {
              setTimeout(() => this._drawConnector(chartContext), 1100)
            }
          }
        },
        plotOptions: {
          bar: {
            borderRadius: 2,
            distributed: true,
            columnWidth: '72%'
          }
        },
        fill: {
          opacity: 0.95
        },
        dataLabels: {
          enabled: true,
          formatter: (val, opts) => opts.dataPointIndex === 17 ? '9.91M' : '',
          offsetY: 120,
          style: {
            fontSize: '11px',
            fontWeight: 700,
            colors: ['#22C55E']
          },
          background: { enabled: false }
        },
        legend: { show: false },
        colors: [
          '#ffffff', '#ffffff', '#ffffff', '#ffffff',
          '#ffffff', '#3B82F6', '#ffffff', '#ffffff',
          '#ffffff', '#ffffff', '#ffffff', '#ffffff',
          '#ffffff', '#ffffff', '#ffffff', '#ffffff',
          '#ffffff', '#22C55E', '#ffffff'
        ],
        stroke: {
          show: true,
          width: 1,
          colors: [
            '#e2e8f0', '#e2e8f0', '#e2e8f0', '#e2e8f0',
            '#e2e8f0', 'transparent', '#e2e8f0', '#e2e8f0',
            '#e2e8f0', '#e2e8f0', '#e2e8f0', '#e2e8f0',
            '#e2e8f0', '#e2e8f0', '#e2e8f0', '#e2e8f0',
            '#e2e8f0', 'transparent', '#e2e8f0'
          ]
        },
        xaxis: {
          categories: [
            '25.2M', '', '', '',
            '', '', '', '',
            '32.2M', '', '', '',
            '', '', '', '',
            '', '39.1M', ''
          ],
          labels: {
            rotate: 0,
            rotateAlways: false,
            style: { fontSize: '10px', colors: '#ffffff' }
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: {
          min: 0,
          max: 45,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        annotations: {
          yaxis: [
            {
              y: 0,
              strokeDashArray: 5,
              borderColor: 'white',
              borderWidth: 1,
              label: {
                text: '0%',
                position: 'left',
                offsetX: -8,
                borderWidth: 0,
                style: {
                  background: 'transparent',
                  color: '#ffffff',
                  fontSize: '10px',
                  padding: { top: 0, bottom: 0, left: 4, right: 4 }
                }
              }
            },
            {
              y: 18,
              strokeDashArray: 5,
              borderColor: 'white',
              borderWidth: 1,
              label: {
                text: '3%',
                position: 'left',
                offsetX: -8,
                borderWidth: 0,
                style: {
                  background: 'transparent',
                  color: '#ffffff',
                  fontSize: '10px',
                  padding: { top: 0, bottom: 0, left: 4, right: 4 }
                }
              }
            },
            {
              y: 36,
              strokeDashArray: 5,
              borderColor: 'white',
              borderWidth: 1,
              label: {
                text: '6%',
                position: 'left',
                offsetX: -8,
                borderWidth: 0,
                style: {
                  background: 'transparent',
                  color: '#ffffff',
                  fontSize: '10px',
                  padding: { top: 0, bottom: 0, left: 4, right: 4 }
                }
              }
            }
          ]
        },
        grid: { show: false },
        tooltip: {
          y: { formatter: (val) => val + 'M' }
        },
        states: {
          hover: { filter: { type: 'lighten', value: 0.05 } }
        }
      }
    }
  },

  mounted () {
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animated = false
            this.$nextTick(() => {
              this.chartKey += 1
              this.animated = true
            })
          } else {
            this.animated = false
          }
        })
      },
      { threshold: 0.3 }
    )
    this._observer.observe(this.$refs.chartRoot)
  },

  beforeDestroy () {
    if (this._observer) this._observer.disconnect()
  },

  methods: {
    _drawConnector (chartContext) {
      if (!chartContext || !chartContext.el) return
      const svgEl = chartContext.el.querySelector('svg')
      if (!svgEl) return

      const existing = svgEl.querySelector('.custom-connector-group')
      if (existing) existing.remove()

      const bars = Array.from(svgEl.querySelectorAll('.apexcharts-bar-area'))
      if (bars.length < 19) return

      const fromBar = bars[5]
      const toBar = bars[17]

      const svgBCR = svgEl.getBoundingClientRect()
      const fromBCR = fromBar.getBoundingClientRect()
      const toBCR = toBar.getBoundingClientRect()

      const x1 = fromBCR.left + fromBCR.width / 2 - svgBCR.left
      const y1 = fromBCR.top - svgBCR.top
      const x2 = toBCR.left + toBCR.width / 2 - svgBCR.left
      const y2 = toBCR.top - 120 - svgBCR.top

      const cx = (x1 + x2) / 2
      const cy = Math.min(y1, y2) - 20

      const ns = 'http://www.w3.org/2000/svg'
      const g = document.createElementNS(ns, 'g')
      g.setAttribute('class', 'custom-connector-group')

      const path = document.createElementNS(ns, 'path')
      path.setAttribute('d', `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`)
      path.setAttribute('stroke', '#ffffff')
      path.setAttribute('stroke-width', '1.5')
      path.setAttribute('stroke-dasharray', '4 3')
      path.setAttribute('fill', 'none')
      g.appendChild(path)

      svgEl.appendChild(g)
    }
  }
}
</script>

<style scoped>
.benchmark-chart-container {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 8px 0;
}

.benchmark-chart-card {
  border-radius: 16px;
  padding: 16px 8px 8px;
}
</style>
